import styled, { css, keyframes } from "styled-components";

export const FeedbackButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  font-family: Barlow;
  font-size: 13px;
  font-weight: 600;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  transition: opacity 0.15s ease;
  white-space: nowrap;

  &:hover {
    opacity: 0.85;
  }
`;

const reveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const focusRing = css`
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

export const ModalContainer = styled.div`
  background: ${({ theme }) => theme.colors.navy600};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  font-family: Barlow;
  gap: 20px;
  max-height: calc(100vh - 32px);
  max-width: 480px;
  overflow-y: auto;
  padding: 24px;
  width: calc(100vw - 32px);

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

export const ModalHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;

export const HeaderTitle = styled.h2`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
`;

export const HeaderSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  line-height: 1.45;
  margin: 6px 0 0;
`;

export const CloseButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  margin: -4px -8px 0 0;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  width: 32px;

  &:hover {
    background: ${({ theme }) => theme.colors.navy300};
    color: ${({ theme }) => theme.colors.ice};
  }

  ${focusRing}
`;

export const Section = styled.div`
  animation: ${reveal} 0.2s ease-out;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const TextArea = styled.textarea`
  background: ${({ theme }) => theme.colors.navy300};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 12px;
  box-sizing: border-box;
  caret-color: ${({ theme }) => theme.colors.envy};
  color: ${({ theme }) => theme.colors.ice};
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.45;
  min-height: 96px;
  outline: none;
  padding: 12px 14px;
  resize: vertical;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.navy200};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    box-shadow: 0 0 0 3px rgba(77, 189, 148, 0.12);
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.chalkAlpha60};
  }
`;

export const CharCount = styled.span<{ $isOver?: boolean }>`
  align-self: flex-end;
  color: ${({ theme, $isOver }) =>
    $isOver ? theme.colors.error : theme.colors.chalk};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
`;

export const ModalFooter = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

export const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  flex: 1;
  font-size: 13px;
  margin: 0;
`;

export const PrimaryButton = styled.button`
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.navy900};
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  padding: 10px 22px;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.darkerEnvy};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.chalkAlpha10};
    color: ${({ theme }) => theme.colors.chalkAlpha40};
    cursor: default;
  }

  ${focusRing}
`;

export const SuccessState = styled.div`
  align-items: center;
  animation: ${reveal} 0.2s ease-out;
  color: ${({ theme }) => theme.colors.ice};
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 0 8px;
  text-align: center;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SuccessBadge = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.envyAlpha20};
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  height: 48px;
  justify-content: center;
  width: 48px;
`;
